import React, { useLayoutEffect, useRef, useState } from 'react';
import type { LabelPlacement, ProductContainerDesign } from '../productContainerTypes';

function LabelZoneFit({ children }: { children: React.ReactNode }) {
  const zoneRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);

  useLayoutEffect(() => {
    const zone = zoneRef.current;
    const content = contentRef.current;
    if (!zone || !content) return;

    const recalc = () => {
      const label = content.querySelector('.product-label-print-target') as HTMLElement | null;
      if (!label) return;
      const sw = zone.clientWidth / Math.max(label.offsetWidth, 1);
      const sh = zone.clientHeight / Math.max(label.offsetHeight, 1);
      setScale(Math.min(1, sw * 0.98, sh * 0.98));
    };

    recalc();
    const observer = new ResizeObserver(recalc);
    observer.observe(zone);
    observer.observe(content);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={zoneRef} className="flex h-full w-full items-center justify-center overflow-hidden">
      <div
        ref={contentRef}
        style={{
          transform: scale < 1 ? `scale(${scale})` : undefined,
          transformOrigin: 'center center',
        }}
      >
        {children}
      </div>
    </div>
  );
}

type Props = {
  container: ProductContainerDesign;
  productImageUrl?: string | null;
  children: React.ReactNode;
  className?: string;
  /** Smaller mockup for storefront card preview */
  compact?: boolean;
};

function labelPositionStyle(placement: LabelPlacement): React.CSSProperties {
  switch (placement) {
    case 'WRAP_AROUND':
      return { left: '8%', right: '8%', top: '28%', bottom: '12%' };
    case 'TOP_LID':
      return { left: '22%', right: '22%', top: '4%', bottom: '72%', transform: 'none' };
    case 'BACK_PANEL':
      return { left: '10%', right: '10%', top: '18%', bottom: '10%' };
    case 'FULL_SURFACE':
      return { inset: '6%' };
    case 'HANG_TAG':
      return { left: '62%', width: '28%', top: '2%', bottom: '35%' };
    case 'FRONT_STICKER':
    default:
      return { left: '14%', right: '14%', top: '22%', bottom: '18%' };
  }
}

function ClearBagShell({
  container,
  productImageUrl,
}: {
  container: ProductContainerDesign;
  productImageUrl?: string | null;
}) {
  return (
    <div
      className="relative h-full w-full overflow-hidden rounded-lg border-2"
      style={{
        borderColor: container.containerAccentColor,
        backgroundColor: `color-mix(in srgb, ${container.containerColor} ${Math.round(container.fillOpacity * 100)}%, transparent)`,
      }}
    >
      {productImageUrl ? (
        <img
          src={productImageUrl}
          alt=""
          className="absolute inset-3 object-cover opacity-90"
          style={{ filter: 'saturate(1.05)' }}
        />
      ) : (
        <div
          className="absolute inset-4 rounded opacity-40"
          style={{ backgroundColor: container.containerAccentColor }}
        />
      )}
      <div className="pointer-events-none absolute inset-x-3 top-2 h-3 rounded-full bg-white/70" />
      <div className="pointer-events-none absolute inset-x-4 bottom-2 h-2 rounded-full bg-white/50" />
    </div>
  );
}

function StandupPouchShell({ container }: { container: ProductContainerDesign }) {
  return (
    <div className="relative mx-auto h-full w-[88%]">
      <div
        className="absolute inset-x-0 top-0 h-[8%] rounded-t-lg border-x-2 border-t-2"
        style={{ borderColor: container.containerAccentColor, backgroundColor: container.lidColor }}
      />
      <div
        className="absolute inset-x-0 top-[7%] bottom-[12%] border-x-2"
        style={{
          borderColor: container.containerAccentColor,
          background: `linear-gradient(180deg, ${container.containerColor}, color-mix(in srgb, ${container.containerAccentColor} 30%, ${container.containerColor}))`,
        }}
      />
      <div
        className="absolute inset-x-[8%] bottom-0 h-[14%]"
        style={{
          background: container.containerColor,
          clipPath: 'polygon(0 0, 100% 0, 88% 100%, 12% 100%)',
          borderBottom: `2px solid ${container.containerAccentColor}`,
        }}
      />
    </div>
  );
}

function JarShell({ container, isGlass }: { container: ProductContainerDesign; isGlass?: boolean }) {
  return (
    <div className="relative mx-auto h-full w-[70%]">
      <div
        className="absolute left-1/2 top-0 h-[12%] w-[55%] -translate-x-1/2 rounded-t-md border-2 border-b-0"
        style={{ backgroundColor: container.lidColor, borderColor: container.containerAccentColor }}
      />
      <div
        className="absolute inset-x-0 top-[10%] bottom-[4%] rounded-b-2xl border-2"
        style={{
          borderColor: container.containerAccentColor,
          background: isGlass
            ? `linear-gradient(90deg, color-mix(in srgb, ${container.containerColor} 70%, white), ${container.containerColor}, color-mix(in srgb, ${container.containerColor} 70%, white))`
            : container.containerColor,
          opacity: isGlass ? 0.85 : 1,
        }}
      />
      <div
        className="absolute inset-x-[18%] top-[28%] bottom-[18%] rounded-full opacity-20"
        style={{ backgroundColor: '#fff' }}
      />
    </div>
  );
}

function BottleShell({ container }: { container: ProductContainerDesign }) {
  return (
    <div className="relative mx-auto h-full w-[42%]">
      <div
        className="absolute left-1/2 top-0 h-[10%] w-[40%] -translate-x-1/2 rounded-t border-2 border-b-0"
        style={{ backgroundColor: container.lidColor, borderColor: container.containerAccentColor }}
      />
      <div
        className="absolute left-1/2 top-[8%] h-[12%] w-[22%] -translate-x-1/2 border-2 border-t-0"
        style={{ backgroundColor: container.containerColor, borderColor: container.containerAccentColor }}
      />
      <div
        className="absolute inset-x-0 top-[18%] bottom-[6%] rounded-b-3xl border-2"
        style={{
          borderColor: container.containerAccentColor,
          background: `linear-gradient(90deg, color-mix(in srgb, ${container.containerColor} 80%, black), ${container.containerColor}, color-mix(in srgb, ${container.containerColor} 80%, black))`,
        }}
      />
    </div>
  );
}

function FoilPouchShell({ container }: { container: ProductContainerDesign }) {
  return (
    <div
      className="relative h-full w-full overflow-hidden rounded-md border-2"
      style={{
        borderColor: container.containerAccentColor,
        background: `linear-gradient(135deg, ${container.lidColor}, ${container.containerColor}, ${container.containerAccentColor}, ${container.containerColor})`,
      }}
    >
      <div className="absolute inset-0 bg-gradient-to-br from-white/30 via-transparent to-black/10" />
      <div className="absolute inset-x-4 top-3 h-2 rounded-full bg-white/40" />
    </div>
  );
}

function BoxShell({ container }: { container: ProductContainerDesign }) {
  return (
    <div
      className="relative mx-auto h-[92%] w-[86%] border-2 shadow-md"
      style={{
        borderColor: container.containerAccentColor,
        backgroundColor: container.containerColor,
      }}
    >
      <div
        className="absolute inset-x-0 top-0 h-[18%] border-b-2"
        style={{ backgroundColor: container.lidColor, borderColor: container.containerAccentColor }}
      />
    </div>
  );
}

function PreprintedBagShell({ container }: { container: ProductContainerDesign }) {
  return (
    <div
      className="relative h-full w-full overflow-hidden rounded-lg border-2"
      style={{
        borderColor: container.containerAccentColor,
        background: `linear-gradient(160deg, ${container.containerColor}, color-mix(in srgb, ${container.containerAccentColor} 40%, ${container.containerColor}))`,
      }}
    >
      <div className="absolute left-3 top-3 text-[8px] font-bold uppercase text-white/90">Brand</div>
      <div className="absolute bottom-3 left-3 right-3 h-[20%] rounded bg-white/15" />
    </div>
  );
}

function TinCanShell({ container }: { container: ProductContainerDesign }) {
  return (
    <div className="relative mx-auto flex h-full w-[72%] flex-col items-center justify-end">
      <div
        className="absolute left-1/2 top-[2%] h-[10%] w-[38%] -translate-x-1/2 rounded-sm border-2"
        style={{ backgroundColor: container.lidColor, borderColor: container.containerAccentColor }}
      />
      <div
        className="relative w-full flex-1 rounded-t-lg rounded-b-2xl border-2"
        style={{
          borderColor: container.containerAccentColor,
          background: `linear-gradient(90deg, color-mix(in srgb, ${container.containerColor} 75%, #71717a), ${container.containerColor}, color-mix(in srgb, ${container.containerColor} 75%, #71717a))`,
          boxShadow: 'inset -4px 0 8px rgba(0,0,0,0.08), inset 4px 0 8px rgba(255,255,255,0.25)',
        }}
      />
      <div
        className="absolute bottom-[8%] left-1/2 h-[3%] w-[85%] -translate-x-1/2 rounded-full opacity-30"
        style={{ backgroundColor: '#000' }}
      />
    </div>
  );
}

function MeshSackShell({
  container,
  productImageUrl,
}: {
  container: ProductContainerDesign;
  productImageUrl?: string | null;
}) {
  return (
    <div
      className="relative mx-auto h-full w-[82%] overflow-hidden rounded-b-3xl rounded-t-lg border-2"
      style={{
        borderColor: container.containerAccentColor,
        backgroundColor: container.containerColor,
        backgroundImage: `repeating-linear-gradient(
          45deg,
          color-mix(in srgb, ${container.containerAccentColor} 18%, transparent),
          color-mix(in srgb, ${container.containerAccentColor} 18%, transparent) 3px,
          transparent 3px,
          transparent 8px
        )`,
      }}
    >
      <div
        className="absolute inset-x-[18%] top-0 h-[14%] rounded-b-full border-2 border-t-0"
        style={{ borderColor: container.containerAccentColor, backgroundColor: container.lidColor }}
      />
      <div
        className="absolute left-1/2 top-[2%] h-[18%] w-[4%] -translate-x-1/2 rounded-full"
        style={{ backgroundColor: container.containerAccentColor }}
      />
      {productImageUrl ? (
        <img
          src={productImageUrl}
          alt=""
          className="absolute inset-x-4 bottom-4 top-[20%] object-cover opacity-80"
          style={{ mixBlendMode: 'multiply' }}
        />
      ) : (
        <div
          className="absolute inset-x-6 bottom-6 top-[24%] rounded-full opacity-30"
          style={{ backgroundColor: container.containerAccentColor }}
        />
      )}
    </div>
  );
}

function ClamshellShell({
  container,
  productImageUrl,
}: {
  container: ProductContainerDesign;
  productImageUrl?: string | null;
}) {
  return (
    <div className="relative mx-auto h-full w-[92%]">
      <div
        className="absolute inset-x-0 top-0 h-[42%] rounded-t-xl border-2 border-b-0"
        style={{
          borderColor: container.containerAccentColor,
          backgroundColor: `color-mix(in srgb, ${container.containerColor} 50%, white)`,
          opacity: 0.85,
        }}
      />
      <div
        className="absolute inset-x-0 bottom-0 top-[38%] rounded-b-xl border-2 border-t-0"
        style={{
          borderColor: container.containerAccentColor,
          backgroundColor: container.containerColor,
        }}
      />
      <div
        className="absolute left-0 right-0 top-[36%] h-[6%] border-y-2"
        style={{ borderColor: container.containerAccentColor, backgroundColor: container.lidColor }}
      />
      {productImageUrl ? (
        <img
          src={productImageUrl}
          alt=""
          className="absolute inset-x-3 bottom-3 top-[44%] object-cover opacity-90"
        />
      ) : null}
    </div>
  );
}

function CanisterShell({ container }: { container: ProductContainerDesign }) {
  return (
    <div className="relative mx-auto h-full w-[58%]">
      <div
        className="absolute left-1/2 top-0 h-[8%] w-[70%] -translate-x-1/2 rounded-t-md border-2 border-b-0"
        style={{ backgroundColor: container.lidColor, borderColor: container.containerAccentColor }}
      />
      <div
        className="absolute inset-x-0 top-[7%] bottom-[5%] rounded-b-lg border-2"
        style={{
          borderColor: container.containerAccentColor,
          background: `linear-gradient(90deg, color-mix(in srgb, ${container.containerColor} 88%, #a3a3a3), ${container.containerColor}, color-mix(in srgb, ${container.containerColor} 88%, #a3a3a3))`,
        }}
      />
    </div>
  );
}

function TubShell({ container }: { container: ProductContainerDesign }) {
  return (
    <div
      className="relative mx-auto h-[75%] w-[88%] rounded-2xl border-2"
      style={{
        borderColor: container.containerAccentColor,
        backgroundColor: container.containerColor,
      }}
    >
      <div
        className="absolute -top-2 inset-x-[10%] h-4 rounded-t-xl border-2 border-b-0"
        style={{ backgroundColor: container.lidColor, borderColor: container.containerAccentColor }}
      />
    </div>
  );
}

export default function ProductContainerPreview({
  container,
  productImageUrl,
  children,
  className = '',
  compact = false,
}: Props) {
  const shell = (() => {
    switch (container.containerType) {
      case 'STANDUP_POUCH':
        return <StandupPouchShell container={container} />;
      case 'FOIL_POUCH':
        return <FoilPouchShell container={container} />;
      case 'PLASTIC_JAR':
        return <JarShell container={container} />;
      case 'GLASS_JAR':
        return <JarShell container={container} isGlass />;
      case 'BOTTLE':
        return <BottleShell container={container} />;
      case 'BOX':
        return <BoxShell container={container} />;
      case 'PREPRINTED_BAG':
        return <PreprintedBagShell container={container} />;
      case 'TUB':
        return <TubShell container={container} />;
      case 'TIN_CAN':
        return <TinCanShell container={container} />;
      case 'MESH_SACK':
        return <MeshSackShell container={container} productImageUrl={productImageUrl} />;
      case 'CLAMSHELL':
        return <ClamshellShell container={container} productImageUrl={productImageUrl} />;
      case 'CANISTER':
        return <CanisterShell container={container} />;
      case 'CLEAR_BAG':
      default:
        return <ClearBagShell container={container} productImageUrl={productImageUrl} />;
    }
  })();

  return (
    <div
      className={`relative mx-auto aspect-[3/4] w-full ${
        compact ? 'max-w-[110px]' : 'max-w-[280px]'
      } ${className}`}
    >
      {shell}
      <div
        className="absolute z-10 overflow-hidden"
        style={labelPositionStyle(container.labelPlacement)}
      >
        <LabelZoneFit>
          <div className="[&_.product-label-print-target]:shadow-none">{children}</div>
        </LabelZoneFit>
      </div>
    </div>
  );
}
